import { webApp } from './telegram';
import { submitOnce } from './submission';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Local development without Telegram: `?dev=123456` (API must allow it). */
function devUserId(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('dev');
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('accept', 'application/json');
  if (init.body) headers.set('content-type', 'application/json');
  const initData = webApp()?.initData ?? '';
  if (initData) headers.set('x-telegram-init-data', initData);
  const dev = devUserId();
  if (!initData && dev) headers.set('x-dev-user-id', dev);

  let res: Response;
  let payload: unknown;
  try {
    res = await fetch(`/api${path}`, { ...init, headers });
    payload = await res.json();
  } catch {
    throw new ApiError('NETWORK_UNCERTAIN', '网络结果尚未确认，请用原内容重试或查看账单', 0);
  }
  if (!res.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(error?.code ?? 'INTERNAL', error?.message ?? '请求失败，请稍后再试', res.status);
  }
  return payload as T;
}

export async function submitOrder<T extends { id: string }>(path: string, body: Record<string, unknown>): Promise<T> {
  const identity = webApp()?.initDataUnsafe?.user?.id ?? devUserId() ?? 'telegram';
  return submitOnce(`${identity}:${path}`, body, async (requestId) => {
    const result = await request<T>(path, {
      method: 'POST', body: JSON.stringify(body), headers: { 'idempotency-key': requestId },
    });
    if (!result || typeof result.id !== 'string' || !result.id) {
      throw new ApiError('NETWORK_UNCERTAIN', '订单结果尚未确认，请用原内容重试或查看账单', 0);
    }
    return result;
  });
}

export const api = {
  deposit: () => request<DepositView>('/deposit'),
  me: () => request<MeResponse>('/me'),
  withdrawView: () => request<WithdrawView>('/withdraw'),
  withdrawHistory: () => request<{ items: WithdrawHistoryItem[] }>('/withdraw/history'),
  submitWithdraw: (body: { address: string; amount: string; pin: string }) =>
    submitOrder<{ id: string; statusText: string; amountText: string; receiveText: string }>('/withdraw', body),
  fx: () => request<FxResponse>('/fx'),
  fxQuote: (body: { direction: string; amount: string }) =>
    request<FxQuote>('/fx/quote', { method: 'POST', body: JSON.stringify(body) }),
  fxOrder: (body: {
    direction: string;
    amount: string;
    payout?: Record<string, string>;
    pin?: string;
  }) =>
    submitOrder<{ id: string; statusText: string; payin: Record<string, string> | null }>('/fx/order', body),
  fxOrders: () => request<{ items: FxOrderView[] }>('/fx/orders'),
  fxMarkPaid: (id: string) => request<{ id: string }>(`/fx/order/${id}/paid`, { method: 'POST' }),
  bills: (type: string, range: string) =>
    request<BillsView>(`/bills?type=${encodeURIComponent(type)}&range=${encodeURIComponent(range)}`),
  security: () => request<SecurityView>('/security'),
  setPin: (body: { pin: string; confirmPin: string; oldPin?: string }) =>
    request<{ ok: true }>('/security/pin', { method: 'POST', body: JSON.stringify(body) }),
  bindAddress: (body: { address: string | null; bindAddressOnly: boolean; pin: string }) =>
    request<SecurityView>('/security/address', { method: 'POST', body: JSON.stringify(body) }),
  setPinFreeLimit: (body: { limit: string; pin: string }) =>
    request<SecurityView>('/security/pin-free-limit', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export interface MeResponse {
  walletId: string;
  nickname: string;
  balanceText: string;
  frozenText: string;
  invites: { total: number; thisWeek: number };
  inviteLink: string;
  brand: { name: string; guideUrl: string };
  security: SecurityView;
}

export interface WithdrawView {
  walletId: string;
  enabled: boolean;
  currency: string;
  balance: number;
  balanceText: string;
  minUsdtText: string;
  maxUsdtText: string;
  feeText: string;
  feeUsdt: number;
  feeBps: number;
  bindAddress: string | null;
  bindAddressOnly: boolean;
  hasPin: boolean;
  fiat: { enabled: boolean; methods: string[]; minUsdtText: string; notice: string };
}

export interface WithdrawHistoryItem {
  id: string;
  address: string;
  amountText: string;
  feeText: string;
  receiveText: string;
  status: 'reviewing' | 'success' | 'rejected' | 'failed';
  statusText: string;
  createdAt: string;
}

export interface FxDirectionInfo {
  direction: 'U2CNY' | 'CNY2U' | 'U2USD' | 'USD2U';
  label: string;
  side: 'sell' | 'buy';
  fiatCurrency: 'CNY' | 'USD';
  enabled: boolean;
  disabledReason: string | null;
  rate: string | null;
  rateText: string | null;
  minText: string;
  feeBps: number;
  payoutMethods: string[];
  payin: Record<string, string> | null;
  quoteTtlSeconds: number;
}

export interface FxResponse {
  directions: FxDirectionInfo[];
  pinFreeLimit: number;
  hasPin: boolean;
  balance: number;
}

export interface FxQuote {
  direction: string;
  label: string;
  side: 'sell' | 'buy';
  fiatCurrency: string;
  rate: string;
  rateText: string;
  usdtAmount: number;
  usdtAmountText: string;
  fiatAmountText: string;
  feeUsdtText: string;
  receiveText: string;
  minText: string;
}

export interface FxOrderView {
  id: string;
  label: string;
  side: 'sell' | 'buy';
  usdtAmountText: string;
  fiatAmountText: string;
  fiatCurrency: string;
  rate: string;
  status: string;
  statusText: string;
  createdAt: string;
}

export interface BillItem {
  id: string;
  type: string;
  typeLabel: string;
  title: string;
  direction: 'in' | 'out';
  signedAmountText: string;
  feeText: string;
  statusText: string;
  createdAt: string;
}

export interface BillsView {
  items: BillItem[];
  emptyText: string;
  totals: { inText: string; outText: string; netText: string };
  filters: {
    types: { key: string; label: string }[];
    ranges: { key: string; label: string }[];
    activeType: string;
    activeRange: string;
  };
}

export interface SecurityView {
  walletId: string;
  hasPin: boolean;
  locked: boolean;
  bindAddress: string | null;
  bindAddressOnly: boolean;
  pinFreeLimit: string;
  notice: string;
}

export interface DepositView { notice?: string | null; mode: string; address: string; qrPayload: string; expectedAmountText: string | null }
